import { ref } from 'vue'
import { useShop } from '@/use/shop'
import {
  GetInvoiceConfig,
  UpdateInvoiceConfig,
  TestPrintInvoice,
  VerifyInvoiceConfig,
  GetSalesInvoiceConfig,
  UpdateSalesInvoiceConfig,
} from '@/api/invoice'

export const useInvoiceConfig = () => {
  const { shopId } = useShop()

  const config = ref(null)
  const salesConfig = ref(null)
  const loading = ref(false)

  /**
   * 取得電子發票設定
   * @returns {Promise<[Object|null, string|null]>} [data, err]
   */
  const fetchConfig = async () => {
    loading.value = true
    try {
      const [data, err] = await GetInvoiceConfig({
        shopId: shopId.value,
      })
      if (err) return [null, err]
      config.value = data
      return [data, null]
    } finally {
      loading.value = false
    }
  }

  /**
   * 更新電子發票設定
   * @param {Object} data - 更新 payload 原樣傳給 API
   * @param {Object} data.invoice - 發票相關設定
   * @param {Object} data.printer - 發票機相關設定
   * @returns {Promise<[Object|null, string|null]>} [res, err]
   */
  const updateConfig = async (data) => {
    loading.value = true
    try {
      const [res, err] = await UpdateInvoiceConfig({
        shopId: shopId.value,
        invoice: data.invoice,
        printer: data.printer,
      })
      if (err) return [null, err]
      config.value = res
      return [res, null]
    } finally {
      loading.value = false
    }
  }

  /**
   * 測試列印發票
   * @returns {Promise<[Object|null, string|null]>} [res, err]
   */
  const testPrint = async () => {
    loading.value = true
    try {
      const [res, err] = await TestPrintInvoice({
        shopId: shopId.value,
      })
      if (err) return [null, err]
      return [res, null]
    } finally {
      loading.value = false
    }
  }

  /**
   * 測試 ezPay 串接資訊是否正確（唯讀，不會開立任何發票）
   * @param {Object} [invoiceApiConfig] - 欲測試的設定 { merchantID, hashKey, hashIV }；未帶則測店家已存設定
   * @returns {Promise<[{valid: boolean, message: string, errorCode?: string}|null, string|null]>} [res, err]
   */
  const verifyConfig = async (invoiceApiConfig) => {
    const [res, err] = await VerifyInvoiceConfig({
      shopId: shopId.value,
      invoiceApiConfig,
    })
    if (err) return [null, err]
    return [res, null]
  }

  /**
   * 取得銷售發票設定
   * @returns {Promise<[Object|null, string|null]>} [data, err]
   */
  const fetchSalesConfig = async () => {
    loading.value = true
    try {
      const [data, err] = await GetSalesInvoiceConfig({
        shopId: shopId.value,
      })
      if (err) return [null, err]
      salesConfig.value = data
      return [data, null]
    } finally {
      loading.value = false
    }
  }

  /**
   * 更新銷售發票設定
   * @param {Object} data - 更新 payload 原樣傳給 API
   * @param {Object} data.defaultInvoiceOption - 出帳彈窗預設選項
   * @param {Object} data.posInvoiceEnabled - POS 發票啟用狀態
   * @returns {Promise<[Object|null, string|null]>} [res, err]
   */
  const updateSalesConfig = async (data) => {
    loading.value = true
    try {
      const [res, err] = await UpdateSalesInvoiceConfig({
        shopId: shopId.value,
        defaultInvoiceOption: data.defaultInvoiceOption,
        posInvoiceEnabled: data.posInvoiceEnabled,
      })
      if (err) return [null, err]
      salesConfig.value = res
      return [res, null]
    } finally {
      loading.value = false
    }
  }

  return {
    config,
    salesConfig,
    loading,
    fetchConfig,
    updateConfig,
    testPrint,
    verifyConfig,
    fetchSalesConfig,
    updateSalesConfig,
  }
}
